import { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import type { TraumaMap } from "../../../shared/src/commands";
import type { ThemeColors } from "../theme";
import { useI18n } from "../i18n";
import { uuidv7 } from "../../../shared/src/ids";
import { POPOVER_BACKDROP, PopoverFooter, popoverBtnStyle } from "../components/PdfForm";
import page1Svg from "../assets/001page1.svg?raw";
import page1DarkSvg from "../assets/001page1dark.svg?raw";

// ─── SVG crop: body diagram area on page 1 ─────────────────────────────
// These are SVG-unit coordinates (viewBox 0 0 595.28 841.89)
const SVG_W = 595.28;
const SVG_H = 841.89;
const CROP = { x: 425, y: 157, w: 154, h: 154 };

// Pre-build the cropped SVG strings at module load:
function cropSvg(raw: string) {
  return raw
    .replace(
      /viewBox="[^"]*"/,
      `viewBox="${CROP.x} ${CROP.y} ${CROP.w} ${CROP.h}"`,
    )
    .replace(/<svg /, '<svg width="100%" height="100%" ');
}
const croppedSvgLight = cropSvg(page1Svg);
const croppedSvgDark = cropSvg(page1DarkSvg);

// ─── BSA zone positions (centered on SVG text labels) ────────────────────
// Computed from SVG translate() coords + font-size 3.7px centering offsets
// "4.5%" text: +4.4px x offset, "9%"/"1%": +2.2px x offset, all: -1.3px y ascent

export interface PdfZonePosition {
  id: string;
  xPct: number; // % of full SVG width (center of text)
  yPct: number; // % of full SVG height (center of text)
  bsa: number; // BSA percentage for this zone
}

// Computed from SVG translate() positions + font metrics:
// fontSize=3.7px, e-Ukraine-Regular, scale(1 1.01)
// Text widths: "4.5%"≈7.76, "9%"≈4.60, "1%"≈3.88
// centerX = translate_x + textWidth/2
// centerY = translate_y - 0.35*fontSize (≈1.30 ascent center)
// xPct = centerX / 595.28 * 100, yPct = centerY / 841.89 * 100
export const PDF_ZONE_POSITIONS: PdfZonePosition[] = [
  // Front body
  { id: "front-head", xPct: 77.74, yPct: 19.79, bsa: 4.5 }, // tr(458.87,167.91)
  { id: "front-chest", xPct: 77.67, yPct: 23.31, bsa: 9 }, // tr(460.06,197.51)
  { id: "front-abdomen", xPct: 77.75, yPct: 26.11, bsa: 9 }, // tr(460.52,221.15)
  { id: "front-genitalia", xPct: 77.72, yPct: 27.9, bsa: 1 }, // tr(460.74,236.21)
  { id: "front-rightArm", xPct: 74.18, yPct: 25.04, bsa: 4.5 }, // tr(437.71,212.10)
  { id: "front-leftArm", xPct: 81.25, yPct: 25.04, bsa: 4.5 }, // tr(479.80,212.10)
  { id: "front-rightLeg", xPct: 76.33, yPct: 30.45, bsa: 9 }, // tr(452.06,257.64)
  { id: "front-leftLeg", xPct: 79.22, yPct: 30.45, bsa: 9 }, // tr(469.29,257.64)
  // Back body
  { id: "back-head", xPct: 90.51, yPct: 19.79, bsa: 4.5 }, // tr(534.90,167.91)
  { id: "back-upperBack", xPct: 90.39, yPct: 23.26, bsa: 9 }, // tr(535.80,197.15)
  { id: "back-lowerBack", xPct: 90.52, yPct: 26.11, bsa: 9 }, // tr(536.57,221.15)
  { id: "back-rightArm", xPct: 86.94, yPct: 25.04, bsa: 4.5 }, // tr(513.63,212.10)
  { id: "back-leftArm", xPct: 94.0, yPct: 25.06, bsa: 4.5 }, // tr(555.70,212.24)
  { id: "back-rightLeg", xPct: 89.07, yPct: 30.45, bsa: 9 }, // tr(527.92,257.68)
  { id: "back-leftLeg", xPct: 91.96, yPct: 30.46, bsa: 9 }, // tr(545.12,257.72)
];

// ─── Trauma cross positions (legacy, kept for backward compat) ──────────

export interface TraumaCrossPosition {
  id: string;
  xPct: number; // % of full SVG width
  yPct: number; // % of full SVG height
}

export const TRAUMA_CROSS_POSITIONS: TraumaCrossPosition[] = [
  // Front body (18)
  { id: "front-t-head", xPct: 77.75, yPct: 18.99 },
  { id: "front-t-neck", xPct: 77.75, yPct: 20.66 },
  { id: "front-t-rChest", xPct: 76.17, yPct: 22.62 },
  { id: "front-t-lChest", xPct: 79.2, yPct: 22.62 },
  { id: "front-t-rUpperArm", xPct: 74.94, yPct: 23.33 },
  { id: "front-t-lUpperArm", xPct: 80.67, yPct: 23.33 },
  { id: "front-t-abdomen", xPct: 77.69, yPct: 24.66 },
  { id: "front-t-rForearm", xPct: 73.6, yPct: 26.44 },
  { id: "front-t-lForearm", xPct: 82.02, yPct: 26.44 },
  { id: "front-t-rHand", xPct: 72.92, yPct: 28.0 },
  { id: "front-t-lHand", xPct: 82.69, yPct: 28.0 },
  { id: "front-t-pelvis", xPct: 77.75, yPct: 27.17 },
  { id: "front-t-rThigh", xPct: 76.18, yPct: 29.06 },
  { id: "front-t-lThigh", xPct: 79.04, yPct: 29.06 },
  { id: "front-t-rLowerLeg", xPct: 76.18, yPct: 31.8 },
  { id: "front-t-lLowerLeg", xPct: 79.04, yPct: 31.8 },
  { id: "front-t-rFoot", xPct: 76.18, yPct: 35.48 },
  { id: "front-t-lFoot", xPct: 79.04, yPct: 35.48 },
  // Back body (18)
  { id: "back-t-head", xPct: 90.57, yPct: 18.99 },
  { id: "back-t-neck", xPct: 90.57, yPct: 20.66 },
  { id: "back-t-rBack", xPct: 88.94, yPct: 22.61 },
  { id: "back-t-lBack", xPct: 91.96, yPct: 22.61 },
  { id: "back-t-rUpperArm", xPct: 87.74, yPct: 23.32 },
  { id: "back-t-lUpperArm", xPct: 93.44, yPct: 23.32 },
  { id: "back-t-lowerBack", xPct: 90.45, yPct: 24.66 },
  { id: "back-t-rForearm", xPct: 86.39, yPct: 26.44 },
  { id: "back-t-lForearm", xPct: 94.79, yPct: 26.44 },
  { id: "back-t-rHand", xPct: 85.72, yPct: 27.98 },
  { id: "back-t-lHand", xPct: 95.46, yPct: 27.98 },
  { id: "back-t-buttock", xPct: 90.45, yPct: 27.63 },
  { id: "back-t-rThigh", xPct: 88.95, yPct: 29.06 },
  { id: "back-t-lThigh", xPct: 91.81, yPct: 29.07 },
  { id: "back-t-rLowerLeg", xPct: 88.95, yPct: 31.8 },
  { id: "back-t-lLowerLeg", xPct: 91.81, yPct: 31.81 },
  { id: "back-t-rFoot", xPct: 88.95, yPct: 35.48 },
  { id: "back-t-lFoot", xPct: 91.81, yPct: 35.49 },
];

// ─── Shared cross mark component ─────────────────────────────────────────

const CROSS_COLOR = "#2563eb";
const CROSS_UNSELECTED_BORDER = "1px solid rgba(100,100,100,0.45)";

/** Renders an × mark that fills its container. */
export function CrossMark({ selected }: { selected: boolean }) {
  const bar: React.CSSProperties = {
    position: "absolute",
    background: selected ? CROSS_COLOR : "transparent",
    border: selected ? "none" : CROSS_UNSELECTED_BORDER,
    borderRadius: 1,
    boxSizing: "border-box",
  };
  return (
    <div style={{ position: "absolute", inset: 0, transform: "rotate(45deg)" }}>
      <div
        style={{ ...bar, left: "15%", right: "15%", top: "40%", height: "20%" }}
      />
      <div
        style={{ ...bar, top: "15%", bottom: "15%", left: "40%", width: "20%" }}
      />
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

/** Convert full-SVG percentage coordinates to crop-relative percentage */
function toCropPct(xPct: number, yPct: number) {
  const svgX = (xPct / 100) * SVG_W;
  const svgY = (yPct / 100) * SVG_H;
  return {
    x: ((svgX - CROP.x) / CROP.w) * 100,
    y: ((svgY - CROP.y) / CROP.h) * 100,
  };
}

/** BSA lookup by zone ID */
const BSA_BY_ID = Object.fromEntries(
  PDF_ZONE_POSITIONS.map((z) => [z.id, z.bsa]),
);

/** Hit radius for injury marks (% of crop width) */
const INJURY_HIT_RADIUS = 3;

// ─── Popover component ──────────────────────────────────────────────────

interface BodyDiagramPopoverProps {
  traumaMap: TraumaMap;
  onConfirm: (updated: TraumaMap) => void;
  onCancel: () => void;
  onChange?: (preview: TraumaMap) => void;
  colors: ThemeColors;
  isDark?: boolean;
}

type InjuryMark = { id: string; x: number; y: number };

export function BodyDiagramPopover({
  traumaMap,
  onConfirm,
  onCancel,
  onChange,
  colors,
  isDark,
}: BodyDiagramPopoverProps) {
  const { t } = useI18n();
  const [tool, setTool] = useState<"burn" | "injury">("injury");
  const [burns, setBurns] = useState<Set<string>>(
    () => new Set(traumaMap.burns),
  );
  const [traumas] = useState<Set<string>>(() => new Set(traumaMap.traumas));
  const [injuries, setInjuries] = useState<InjuryMark[]>(() => [
    ...(traumaMap.injuries || []),
  ]);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);
  const didDragRef = useRef(false);

  const notify = useCallback(
    (nextBurns: Set<string>, nextInjuries: InjuryMark[]) => {
      onChange?.({
        ...traumaMap,
        burns: [...nextBurns],
        traumas: [...traumas],
        injuries: nextInjuries,
      });
    },
    [onChange, traumaMap, traumas],
  );

  const toggleBurn = (id: string) => {
    setBurns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      notify(next, injuries);
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm({
      ...traumaMap,
      burns: [...burns],
      traumas: [...traumas],
      injuries,
    });
  };

  /** Convert a mouse/touch event to crop-relative % */
  const eventToCropPct = (
    clientX: number,
    clientY: number,
  ): { x: number; y: number } | null => {
    const el = svgContainerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return null;
    return { x, y };
  };

  /** Find nearest injury within hit radius */
  const findNearInjury = (x: number, y: number): string | null => {
    // Scale y hit radius by aspect ratio so it's visually circular
    const yRadius = (INJURY_HIT_RADIUS * CROP.w) / CROP.h;
    for (const m of injuries) {
      const dx = m.x - x;
      const dy = m.y - y;
      if (Math.abs(dx) < INJURY_HIT_RADIUS && Math.abs(dy) < yRadius)
        return m.id;
    }
    return null;
  };

  const handleInjuryClick = (e: React.MouseEvent) => {
    if (tool !== "injury") return;
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    const pos = eventToCropPct(e.clientX, e.clientY);
    if (!pos) return;
    const near = findNearInjury(pos.x, pos.y);
    if (near) {
      // Remove existing
      setInjuries((prev) => {
        const next = prev.filter((m) => m.id !== near);
        notify(burns, next);
        return next;
      });
    } else {
      // Add new
      const mark: InjuryMark = { id: uuidv7(), x: pos.x, y: pos.y };
      setInjuries((prev) => {
        const next = [...prev, mark];
        notify(burns, next);
        return next;
      });
    }
  };

  // ─── Drag support for injury marks ─────────────────────────────────

  const handleInjuryPointerDown = (e: React.PointerEvent, markId: string) => {
    if (tool !== "injury") return;
    e.stopPropagation();
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const mark = injuries.find((m) => m.id === markId);
    if (!mark) return;
    draggingRef.current = {
      id: markId,
      startX: e.clientX,
      startY: e.clientY,
      origX: mark.x,
      origY: mark.y,
    };
    didDragRef.current = false;
  };

  const handleInjuryPointerMove = (e: React.PointerEvent) => {
    const drag = draggingRef.current;
    if (!drag) return;
    const el = svgContainerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = ((e.clientX - drag.startX) / rect.width) * 100;
    const dy = ((e.clientY - drag.startY) / rect.height) * 100;
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) didDragRef.current = true;
    const nx = Math.max(0, Math.min(100, drag.origX + dx));
    const ny = Math.max(0, Math.min(100, drag.origY + dy));
    setInjuries((prev) =>
      prev.map((m) => (m.id === drag.id ? { ...m, x: nx, y: ny } : m)),
    );
  };

  const handleInjuryPointerUp = () => {
    if (draggingRef.current) {
      notify(burns, injuries);
      draggingRef.current = null;
    }
  };

  const totalBsa = [...burns].reduce(
    (sum, id) => sum + (BSA_BY_ID[id] || 0),
    0,
  );

  const burnHitSize = 7; // % of crop dimensions
  const crossHitSize = 5;

  // Responsive popover width: fill screen but constrain by height too
  const cropAspect = (CROP.w / CROP.h).toFixed(4);

  const hasAny = burns.size > 0 || traumas.size > 0 || injuries.length > 0;

  const toolBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: "0.4em 1em",
    fontSize: "0.85em",
    background: active ? colors.primary : "transparent",
    color: active ? "#fff" : colors.textMuted,
    border: active ? "none" : `1px solid ${colors.border}`,
    borderRadius: "0.25em",
    cursor: "pointer",
    fontWeight: active ? 600 : 400,
    fontFamily: "system-ui, sans-serif",
  });

  return createPortal(
    <div
      style={POPOVER_BACKDROP}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleConfirm();
      }}
    >
      <div
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: "0.5em",
          padding: "0.75em",
          boxShadow: "0 0.5em 2em rgba(0,0,0,0.3)",
          width: `min(95vw, calc((95vh - 7em) * ${cropAspect}), 960px)`,
          maxHeight: "95vh",
          display: "flex",
          flexDirection: "column",
          fontSize: "1rem",
          lineHeight: "normal",
        }}
      >
        {/* Title */}
        <div
          style={{ textAlign: "center", marginBottom: "0.5em", flexShrink: 0 }}
        >
          <span
            style={{ fontWeight: 600, color: colors.text, fontSize: "0.85em" }}
          >
            {t("body.title")}
          </span>
        </div>

        {/* Cropped SVG with clickable overlays */}
        <div
          ref={svgContainerRef}
          onClick={handleInjuryClick}
          onPointerMove={handleInjuryPointerMove}
          onPointerUp={handleInjuryPointerUp}
          style={{
            position: "relative",
            width: "100%",
            aspectRatio: `${CROP.w} / ${CROP.h}`,
            overflow: "hidden",
            borderRadius: "0.25em",
            flexShrink: 0,
            cursor: tool === "injury" ? "crosshair" : "default",
          }}
        >
          {/* The actual form SVG, cropped via modified viewBox */}
          <div
            dangerouslySetInnerHTML={{ __html: isDark ? croppedSvgDark : croppedSvgLight }}
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              userSelect: "none",
            }}
          />

          {/* Burn zone overlays (BSA circles) */}
          {PDF_ZONE_POSITIONS.map((z) => {
            const pos = toCropPct(z.xPct, z.yPct);
            const selected = burns.has(z.id);
            return (
              <div
                key={z.id}
                onClick={
                  tool === "burn"
                    ? (e) => {
                        e.stopPropagation();
                        toggleBurn(z.id);
                      }
                    : undefined
                }
                style={{
                  position: "absolute",
                  left: `${pos.x - burnHitSize / 2}%`,
                  top: `${pos.y - burnHitSize / 2}%`,
                  width: `${burnHitSize}%`,
                  height: `${(burnHitSize * CROP.w) / CROP.h}%`,
                  cursor: tool === "burn" ? "pointer" : "default",
                  pointerEvents: tool === "burn" ? "auto" : "none",
                  zIndex: 2,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: "10%",
                    borderRadius: "50%",
                    border: selected
                      ? "2.5px solid #dc2626"
                      : "1.5px solid rgba(120,120,120,0.45)",
                    background: selected
                      ? "rgba(220,38,38,0.15)"
                      : "transparent",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            );
          })}

          {/* Legacy trauma cross overlays (read-only display for old data) */}
          {traumas.size > 0 &&
            TRAUMA_CROSS_POSITIONS.map((z) => {
              const pos = toCropPct(z.xPct, z.yPct);
              if (!traumas.has(z.id)) return null;
              return (
                <div
                  key={z.id}
                  style={{
                    position: "absolute",
                    left: `${pos.x}%`,
                    top: `${pos.y}%`,
                    transform: "translate(-50%, -50%)",
                    width: `${crossHitSize}%`,
                    height: `${(crossHitSize * CROP.w) / CROP.h}%`,
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                >
                  <CrossMark selected />
                </div>
              );
            })}

          {/* Free-form injury marks */}
          {injuries.map((m) => (
            <div
              key={m.id}
              onPointerDown={(e) => handleInjuryPointerDown(e, m.id)}
              style={{
                position: "absolute",
                left: `${m.x}%`,
                top: `${m.y}%`,
                transform: "translate(-50%, -50%)",
                width: `${crossHitSize}%`,
                height: `${(crossHitSize * CROP.w) / CROP.h}%`,
                cursor: tool === "injury" ? "grab" : "default",
                pointerEvents: tool === "injury" ? "auto" : "none",
                zIndex: 3,
                touchAction: "none",
              }}
            >
              <CrossMark selected />
            </div>
          ))}
        </div>

        {/* Status + buttons */}
        <div
          style={{
            textAlign: "center",
            marginTop: "0.5em",
            fontSize: "0.7em",
            color: colors.textMuted,
            flexShrink: 0,
          }}
        >
          {hasAny
            ? [
                burns.size > 0 &&
                  t("body.burnZones", {
                    count: String(burns.size),
                    total: String(PDF_ZONE_POSITIONS.length),
                    bsa: String(totalBsa),
                  }),
                injuries.length > 0 &&
                  t("body.injuryMarks", { count: String(injuries.length) }),
                traumas.size > 0 &&
                  t("body.legacyTrauma", { count: String(traumas.size) }),
              ]
                .filter(Boolean)
                .join(" \u00b7 ")
            : tool === "burn"
              ? t("body.burnHint")
              : t("body.injuryHint")}
        </div>
        <PopoverFooter
          onClear={() => onConfirm({ ...traumaMap, burns: [], traumas: [], injuries: [] })}
          onCommit={handleConfirm}
          colors={colors}
          noBorder
        >
          <button
            onClick={() => { setBurns(new Set()); setInjuries([]); }}
            disabled={!hasAny}
            style={{
              ...popoverBtnStyle(colors),
              cursor: !hasAny ? "default" : "pointer",
              color: !hasAny ? colors.border : colors.textMuted,
            }}
          >
            {t("common.reset")}
          </button>
          <div style={{ display: "flex", gap: "0.25em" }}>
            <button onClick={() => setTool("injury")} style={toolBtnStyle(tool === "injury")}>
              {"\u2716"} {t("body.injury")}
            </button>
            <button onClick={() => setTool("burn")} style={toolBtnStyle(tool === "burn")}>
              {"\u25CB"} {t("body.burn")}
            </button>
          </div>
        </PopoverFooter>
      </div>
    </div>,
    document.body,
  );
}
